import { inject } from '@angular/core';
import { CanActivateFn, Router } from '@angular/router';
import { JobsService } from '../services/jobs.service';
import { VerificationCacheService } from '../services/verification-cache.service';

/**
 * Route guard that enforces the verify → attempt navigation flow.
 *
 * @remarks
 * When a job has `verCodes` (service documents that require scanning), the
 * process server must pass through `/verify/:id` before the attempt log.
 * Bypassing verify (e.g. direct URL access) results in an attempt submission
 * with no attached photos, which the server rejects with a 400 error.
 *
 * **Guard logic:**
 * 1. If the job is not in cache (app reloaded before sync) → `/jobs`.
 * 2. If the job has no `verCodes` (no document scan required) → allow through.
 * 3. If all verification-required defendants are in `VerificationCacheService`
 *    (user completed verify in this session) → allow through.
 * 4. Otherwise → `/verify/:id`.
 *
 * @example
 * ```ts
 * // In app.routes.ts
 * { path: 'attempt/:id', canActivate: [authGuard, attemptGuard] }
 * ```
 */
export const attemptGuard: CanActivateFn = (route) => {
  const jobsService = inject(JobsService);
  const verifCache  = inject(VerificationCacheService);
  const router      = inject(Router);

  const jobId = route.paramMap.get('id') ?? '';
  const job   = jobsService.getJob(jobId);

  // Job not found (e.g. direct URL access before sync completes) — send to job list.
  if (!job) return router.createUrlTree(['/jobs']);

  // No service documents on this job — verification not required, allow through.
  if (!job.verCodes.some(Boolean)) return true;

  // Check that every defendant requiring a scan was verified this session.
  const verifiedIndices = verifCache.getVerifiedIndices(jobId);
  const allVerified     = job.defs.every(
    (_, i) => !job.verCodes[i] || verifiedIndices.includes(i),
  );

  return allVerified ? true : router.createUrlTree(['/verify', jobId]);
};
